import { uiGalleryRoute } from '@fcms/ui/gallery'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { PERMISSIONS } from '@/auth/permissions'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'

declare module 'vue-router' {
  interface RouteMeta {
    /** Shown in the tab title and, where a view does not set its own, the H1. */
    title?: string
    /** Reachable without a session. */
    publicRoute?: boolean
    /**
     * The permission this screen needs to be USEFUL. The guard redirects a
     * user without it to a screen they can use — it is not an access control,
     * because the API enforces the real one on every request.
     */
    permission?: string
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    component: () => import('@/layouts/AuthLayout.vue'),
    children: [
      {
        path: '',
        name: 'login',
        component: () => import('@/views/auth/LoginView.vue'),
        meta: { title: 'Sign in', publicRoute: true },
      },
      {
        path: 'totp',
        name: 'login-totp',
        component: () => import('@/views/auth/TotpView.vue'),
        meta: { title: 'Two-factor code', publicRoute: true },
      },
    ],
  },

  {
    path: '/',
    component: () => import('@/layouts/AppShell.vue'),
    children: [
      { path: '', name: 'home', redirect: { name: 'landing' } },

      // A synthetic route: which dashboard is "home" depends on the role, and
      // resolving that in a redirect function keeps the answer in one place.
      {
        path: 'start',
        name: 'landing',
        redirect: () => {
          const auth = useAuthStore()
          if (auth.can(PERMISSIONS.dashboardMD)) return { name: 'dashboard-executive' }
          if (auth.can(PERMISSIONS.reportRead)) return { name: 'dashboard-operations' }
          if (auth.can(PERMISSIONS.collectionRead)) return { name: 'sessions' }
          if (auth.can(PERMISSIONS.billingRead)) return { name: 'bills' }
          return { name: 'my-sessions' }
        },
      },

      // --- dashboards ------------------------------------------------------
      {
        path: 'dashboard/executive',
        name: 'dashboard-executive',
        component: () => import('@/views/dashboard/ExecutiveDashboardView.vue'),
        meta: { title: 'Executive dashboard', permission: PERMISSIONS.dashboardMD },
      },
      {
        path: 'dashboard/operations',
        name: 'dashboard-operations',
        component: () => import('@/views/dashboard/OperationsDashboardView.vue'),
        meta: { title: 'Operations', permission: PERMISSIONS.reportRead },
      },

      // --- collection ------------------------------------------------------
      {
        path: 'collection/sessions',
        name: 'sessions',
        component: () => import('@/views/collection/SessionListView.vue'),
        meta: { title: 'Collection sessions', permission: PERMISSIONS.collectionRead },
      },
      {
        path: 'collection/sessions/:id',
        name: 'session-detail',
        component: () => import('@/views/collection/SessionDetailView.vue'),
        meta: { title: 'Session', permission: PERMISSIONS.collectionRead },
      },
      {
        path: 'collection/manual-entry',
        name: 'manual-entry',
        component: () => import('@/views/collection/ManualEntryView.vue'),
        meta: { title: 'Manual entry', permission: PERMISSIONS.collectionManualEntry },
      },
      {
        path: 'collection/queue',
        name: 'queue',
        component: () => import('@/views/collection/QueueBoardView.vue'),
        meta: { title: 'Queue board', permission: PERMISSIONS.collectionRead },
      },

      // --- devices ---------------------------------------------------------
      {
        path: 'devices',
        name: 'devices',
        component: () => import('@/views/devices/DeviceRegistryView.vue'),
        meta: { title: 'Device registry', permission: PERMISSIONS.deviceRead },
      },
      {
        path: 'devices/:id',
        name: 'device-detail',
        component: () => import('@/views/devices/DeviceDetailView.vue'),
        meta: { title: 'Device', permission: PERMISSIONS.deviceRead },
      },
      {
        path: 'devices-health',
        name: 'device-health',
        component: () => import('@/views/devices/DeviceHealthView.vue'),
        meta: { title: 'Fleet health', permission: PERMISSIONS.deviceRead },
      },
      {
        path: 'devices-conflicts',
        name: 'ingest-conflicts',
        component: () => import('@/views/devices/IngestConflictView.vue'),
        meta: { title: 'Ingest conflicts', permission: PERMISSIONS.ingestResolve },
      },
      {
        path: 'devices-csv-import',
        name: 'csv-import',
        component: () => import('@/views/devices/CsvImportView.vue'),
        meta: { title: 'CSV import', permission: PERMISSIONS.deviceManage },
      },

      // --- billing ---------------------------------------------------------
      {
        path: 'billing/bills',
        name: 'bills',
        component: () => import('@/views/billing/BillListView.vue'),
        meta: { title: 'Bills', permission: PERMISSIONS.billingRead },
      },
      {
        path: 'billing/bills/:id',
        name: 'bill-detail',
        component: () => import('@/views/billing/BillDetailView.vue'),
        meta: { title: 'Bill', permission: PERMISSIONS.billingRead },
      },
      {
        path: 'billing/invoices',
        name: 'invoices',
        component: () => import('@/views/billing/InvoiceListView.vue'),
        meta: { title: 'Invoices', permission: PERMISSIONS.billingRead },
      },
      {
        path: 'billing/invoices/:id',
        name: 'invoice-detail',
        component: () => import('@/views/billing/InvoiceDetailView.vue'),
        meta: { title: 'Invoice', permission: PERMISSIONS.billingRead },
      },
      {
        path: 'billing/rates',
        name: 'rates',
        component: () => import('@/views/billing/RateAdminView.vue'),
        meta: { title: 'Rates', permission: PERMISSIONS.billingRead },
      },

      // --- finance ---------------------------------------------------------
      {
        path: 'finance/wallets',
        name: 'wallets',
        component: () => import('@/views/finance/WalletAccountsView.vue'),
        meta: { title: 'Wallet accounts', permission: PERMISSIONS.walletRead },
      },
      {
        path: 'finance/wallets/:id',
        name: 'wallet-statement',
        component: () => import('@/views/finance/WalletStatementView.vue'),
        meta: { title: 'Statement', permission: PERMISSIONS.walletRead },
      },
      {
        path: 'finance/journals',
        name: 'journals',
        component: () => import('@/views/finance/JournalListView.vue'),
        meta: { title: 'Journals', permission: PERMISSIONS.walletRead },
      },
      {
        path: 'finance/ledger-check',
        name: 'ledger-check',
        component: () => import('@/views/finance/LedgerCheckView.vue'),
        meta: { title: 'Ledger check', permission: PERMISSIONS.walletRead },
      },
      {
        path: 'finance/payouts',
        name: 'payouts',
        component: () => import('@/views/finance/PayoutRunListView.vue'),
        meta: { title: 'Payout runs', permission: PERMISSIONS.payoutRead },
      },
      {
        path: 'finance/payouts/:id',
        name: 'payout-detail',
        component: () => import('@/views/finance/PayoutRunDetailView.vue'),
        meta: { title: 'Payout run', permission: PERMISSIONS.payoutRead },
      },

      // --- sports ----------------------------------------------------------
      {
        path: 'sports/requirements',
        name: 'sports-requirements',
        component: () => import('@/views/sports/RequirementListView.vue'),
        meta: { title: 'Purchase requirements', permission: PERMISSIONS.sportsRead },
      },
      {
        path: 'sports/requirements/:id',
        name: 'sports-requirement-detail',
        component: () => import('@/views/sports/RequirementDetailView.vue'),
        meta: { title: 'Requirement', permission: PERMISSIONS.sportsRead },
      },
      {
        path: 'sports/registrations',
        name: 'sports-registrations',
        component: () => import('@/views/sports/RegistrationLookupView.vue'),
        meta: { title: 'Sports registrations', permission: PERMISSIONS.sportsRead },
      },
      {
        path: 'sports/quota-tokens',
        name: 'sports-quota-tokens',
        component: () => import('@/views/sports/QuotaTokenView.vue'),
        meta: { title: 'Quota tokens', permission: PERMISSIONS.sportsRead },
      },

      // --- reports ---------------------------------------------------------
      {
        path: 'reports/pack',
        name: 'report-pack',
        component: () => import('@/views/reports/ManagementPackView.vue'),
        meta: { title: 'Management pack', permission: PERMISSIONS.reportRead },
      },
      {
        path: 'reports/transactions',
        name: 'report-transactions',
        component: () => import('@/views/reports/TransactionSearchView.vue'),
        meta: { title: 'Transaction search', permission: PERMISSIONS.reportRead },
      },
      {
        path: 'reports/vessels',
        name: 'report-vessels',
        component: () => import('@/views/reports/VesselActivityView.vue'),
        meta: { title: 'Vessel activity', permission: PERMISSIONS.reportRead },
      },
      {
        path: 'reports/audit',
        name: 'report-audit',
        component: () => import('@/views/reports/AuditBrowserView.vue'),
        meta: { title: 'Audit trail', permission: PERMISSIONS.auditRead },
      },
      {
        path: 'reports/audit/:id',
        name: 'report-audit-detail',
        component: () => import('@/views/reports/AuditEventView.vue'),
        meta: { title: 'Audit event', permission: PERMISSIONS.auditRead },
      },

      // --- integrations ----------------------------------------------------
      {
        path: 'integrations/partners',
        name: 'partners',
        component: () => import('@/views/integrations/PartnerListView.vue'),
        meta: { title: 'Partners', permission: PERMISSIONS.integrationRead },
      },
      {
        path: 'integrations/api-keys',
        name: 'api-keys',
        component: () => import('@/views/integrations/ApiKeyListView.vue'),
        meta: { title: 'API keys', permission: PERMISSIONS.integrationRead },
      },
      {
        path: 'integrations/api-keys/:id',
        name: 'api-key-detail',
        component: () => import('@/views/integrations/ApiKeyDetailView.vue'),
        meta: { title: 'API key', permission: PERMISSIONS.integrationRead },
      },
      {
        path: 'integrations/request-log',
        name: 'request-log',
        component: () => import('@/views/integrations/RequestLogView.vue'),
        meta: { title: 'Request log', permission: PERMISSIONS.integrationRead },
      },
      {
        path: 'integrations/webhooks',
        name: 'webhooks',
        component: () => import('@/views/integrations/WebhookListView.vue'),
        meta: { title: 'Webhooks', permission: PERMISSIONS.integrationRead },
      },
      {
        path: 'integrations/webhooks/:id',
        name: 'webhook-detail',
        component: () => import('@/views/integrations/WebhookDetailView.vue'),
        meta: { title: 'Webhook', permission: PERMISSIONS.integrationRead },
      },
      {
        path: 'integrations/api-docs',
        name: 'api-docs',
        component: () => import('@/views/integrations/ApiDocsView.vue'),
        meta: { title: 'Partner API documentation', permission: PERMISSIONS.integrationRead },
      },
      {
        path: 'integrations/erp',
        name: 'erp-mapping',
        component: () => import('@/views/integrations/ErpExportView.vue'),
        meta: { title: 'ERP & exports', permission: PERMISSIONS.integrationRead },
      },

      // --- master data -----------------------------------------------------
      {
        path: 'masterdata/collection-points',
        name: 'md-collection-points',
        component: () => import('@/views/masterdata/CollectionPointListView.vue'),
        meta: { title: 'Collection points', permission: PERMISSIONS.masterdataRead },
      },
      {
        path: 'masterdata/vessels',
        name: 'md-vessels',
        component: () => import('@/views/masterdata/VesselListView.vue'),
        meta: { title: 'Vessels', permission: PERMISSIONS.masterdataRead },
      },
      {
        path: 'masterdata/vessels/:id/crew',
        name: 'md-vessel-crew',
        component: () => import('@/views/masterdata/VesselCrewView.vue'),
        meta: { title: 'Crew', permission: PERMISSIONS.masterdataRead },
      },
      {
        path: 'masterdata/fishermen',
        name: 'md-fishermen',
        component: () => import('@/views/masterdata/FishermanListView.vue'),
        meta: { title: 'Fishermen', permission: PERMISSIONS.masterdataRead },
      },
      {
        path: 'masterdata/fish-types',
        name: 'md-fish-types',
        component: () => import('@/views/masterdata/FishTypeListView.vue'),
        meta: { title: 'Fish types', permission: PERMISSIONS.masterdataRead },
      },
      {
        path: 'masterdata/fish-types/:id/grades',
        name: 'md-fish-grades',
        component: () => import('@/views/masterdata/FishGradeListView.vue'),
        meta: { title: 'Grades', permission: PERMISSIONS.masterdataRead },
      },
      {
        path: 'masterdata/scales',
        name: 'md-scales',
        component: () => import('@/views/masterdata/ScaleListView.vue'),
        meta: { title: 'Scales', permission: PERMISSIONS.masterdataRead },
      },

      // --- assistant -------------------------------------------------------
      {
        path: 'assistant',
        name: 'assistant',
        component: () => import('@/views/assistant/AssistantView.vue'),
        meta: { title: 'Assistant', permission: PERMISSIONS.assistantUse },
      },
      {
        path: 'assistant/brief',
        name: 'assistant-brief',
        component: () => import('@/views/assistant/BriefView.vue'),
        meta: { title: 'Daily brief', permission: PERMISSIONS.assistantUse },
      },

      // --- portal administration + personal settings ------------------------
      {
        path: 'settings/announcements',
        name: 'announcements',
        component: () => import('@/views/portal/AnnouncementListView.vue'),
        meta: { title: 'Announcements', permission: PERMISSIONS.settingsManage },
      },
      {
        path: 'settings/profile-changes',
        name: 'profile-changes',
        component: () => import('@/views/portal/ProfileChangeView.vue'),
        meta: { title: 'Profile change requests', permission: PERMISSIONS.settingsManage },
      },
      {
        path: 'settings/sessions',
        name: 'my-sessions',
        component: () => import('@/views/settings/MySessionsView.vue'),
        meta: { title: 'My sessions' },
      },
      {
        path: 'settings/security',
        name: 'my-security',
        component: () => import('@/views/settings/SecurityView.vue'),
        meta: { title: 'Two-factor authentication' },
      },

      {
        path: 'forbidden',
        name: 'forbidden',
        component: () => import('@/views/ForbiddenView.vue'),
        meta: { title: 'Not available to you' },
      },
      {
        path: ':pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/views/NotFoundView.vue'),
        meta: { title: 'Page not found' },
      },
    ],
  },

  // The design system's own gallery. Lazily imported by the package, so it
  // never lands in the initial chunk; it is what the screenshot matrix
  // captures for the primitives themselves.
  uiGalleryRoute,
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  // Back/forward restores the previous scroll position; a fresh navigation
  // starts at the top. A dense table that keeps its scroll offset across an
  // unrelated navigation looks like a rendering fault.
  scrollBehavior(_to, _from, saved) {
    return saved ?? { top: 0 }
  },
})

/**
 * One guard, three jobs: resolve the session once, keep signed-out users on
 * the sign-in screen, and send a user who cannot use a screen somewhere they
 * can rather than showing them an empty page that will 403 on every request.
 */
router.beforeEach(async (to) => {
  const auth = useAuthStore()

  if (!auth.ready) {
    try {
      await auth.loadMe()
    } catch {
      // A network failure while resolving the session is not a signed-out
      // state. The shell renders and each screen reports its own error; the
      // alternative is bouncing a working session to the sign-in screen every
      // time an island link stutters.
    }
  }

  if (to.meta.publicRoute) {
    // A signed-in user who lands on /login has almost certainly used a
    // bookmark. Send them to their own landing screen.
    if (auth.signedIn) return { name: 'landing' }
    return true
  }

  if (!auth.signedIn) {
    return { name: 'login', query: to.fullPath === '/' ? {} : { next: to.fullPath } }
  }

  // A site-restricted principal (e.g. a site supervisor) must have their own
  // site selected before any site-scoped screen requests. The collection
  // module 403s a site-restricted request that omits `collection_point_id`
  // rather than narrowing it implicitly, so this cannot wait for the user to
  // open the site switcher themselves.
  if (auth.siteRestricted) {
    useUiStore().ensureSiteInScope(auth.siteScope)
  }

  const needed = to.meta.permission
  if (needed && !auth.can(needed)) {
    return { name: 'forbidden', query: { from: to.fullPath } }
  }

  return true
})

router.afterEach((to) => {
  const title = to.meta.title
  document.title = title ? `${title} · FCMS` : 'MIFCO FCMS — Back office'
})

export default router
